import moment from "moment";
import React from "react";
import { AbsoluteFill, Easing, Img, interpolate } from "remotion";
import { Glyph, PAPER, TEAL, blurredMap } from "./houseStyle";
import { scatteredRandom } from "./introDrawing";
import {
	IntroLayout,
	PASSPORT_PAGES,
	PF_DROP,
	PF_EVERY,
	PF_LEAVE,
	PF_OPEN,
	PF_STAMPS,
	PF_TURN,
	PP_EVERY,
	PP_LEAVE,
	PP_MONTAGE,
	PP_SHOT,
	PP_SHOTS,
	overlayBox,
} from "./introCamera";
import { PassportStamp, StampShape, leafZ, montageShots, passportStamps, pickEvenly, shakeAt, slamAt, stampHits } from "./passportStamps";
import { mediaUrl } from "./reelMedia";

/**
 * The two passport intros, drawn over the map while the camera holds the whole trip behind them (see
 * `HOLDS_THE_TRIP` in introCamera.ts). What is stamped and when is passportStamps.ts; this only draws.
 * The reel's own header is laid over both, as over every other intro.
 */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ease = (frame: number, window: number[], easing: (t: number) => number = Easing.inOut(Easing.cubic)) =>
	interpolate(frame, window, [0, 1], { ...clamp, easing });

const GOLD = "#e8c872";

// ---- one stamp ------------------------------------------------------------------------------------

const VIEWBOX: Record<StampShape, [number, number]> = { circle: [200, 200], rect: [260, 160], octagon: [200, 200] };
const octagon = (r: number) =>
	Array.from({ length: 8 }, (_, i) => {
		const a = Math.PI / 8 + (i * Math.PI) / 4;
		return `${(100 + r * Math.cos(a)).toFixed(1)},${(100 + r * Math.sin(a)).toFixed(1)}`;
	}).join(" ");

/**
 * A rubber stamp: a double border, the stop's name, its date and nights, and in the middle how you got
 * there. Inked through a grain mask, a soft pressure mask and a slight warp, so no edge is clean and
 * no two stamps wear the same.
 */
const Stamp = ({
	stamp,
	width,
	id,
	seed,
	ink = stamp.ink,
	specks = true,
}: {
	stamp: PassportStamp;
	width: number;
	id: string;
	seed: number;
	ink?: string;
	specks?: boolean;
}) => {
	const { shape } = stamp;
	const [vbW, vbH] = VIEWBOX[shape];
	const text = (size: number, weight = 800) => ({ fontFamily: "Poppins", fontWeight: weight, fontSize: size, fill: ink, textAnchor: "middle" as const });
	const len = Math.max(4, stamp.city.length);
	const line = `${stamp.when} · ${stamp.nights}`;
	// How you arrived, or ENTRY on the first stop, centred on (x, y).
	const arrival = (x: number, y: number, size: number) =>
		stamp.arrivedBy ? (
			<g transform={`translate(${x} ${y})`}>
				<Glyph name={stamp.arrivedBy} size={size} color={ink} />
			</g>
		) : (
			<text {...text(size * 0.36)} x={x} y={y + size * 0.13} letterSpacing={size * 0.04}>
				ENTRY
			</text>
		);

	let body: React.ReactNode;
	if (shape === "circle") {
		body = (
			<>
				<circle cx={100} cy={100} r={93} fill="none" stroke={ink} strokeWidth={7} />
				<circle cx={100} cy={100} r={81} fill="none" stroke={ink} strokeWidth={2.5} />
				{/* The name round the top, the date round the bottom, both reading left to right. */}
				<path id={`${id}-top`} d="M 42 100 A 58 58 0 0 1 158 100" fill="none" />
				<path id={`${id}-bottom`} d="M 31 100 A 69 69 0 0 0 169 100" fill="none" />
				<text {...text(Math.min(28, 250 / len))} letterSpacing={1}>
					<textPath href={`#${id}-top`} startOffset="50%">
						{stamp.city}
					</textPath>
				</text>
				<text {...text(14, 700)} letterSpacing={1.5}>
					<textPath href={`#${id}-bottom`} startOffset="50%">
						{line}
					</textPath>
				</text>
				<circle cx={33} cy={100} r={4} fill={ink} />
				<circle cx={167} cy={100} r={4} fill={ink} />
				{arrival(100, 100, 52)}
			</>
		);
	} else if (shape === "rect") {
		body = (
			<>
				<rect x={5} y={5} width={250} height={150} rx={18} fill="none" stroke={ink} strokeWidth={7} />
				<rect x={15} y={15} width={230} height={130} rx={10} fill="none" stroke={ink} strokeWidth={2.5} />
				{arrival(130, 39, 34)}
				<text {...text(Math.min(46, 290 / len))} x={130} y={101}>
					{stamp.city}
				</text>
				<text {...text(15, 700)} letterSpacing={2} x={130} y={132}>
					{line}
				</text>
			</>
		);
	} else {
		body = (
			<>
				<polygon points={octagon(93)} fill="none" stroke={ink} strokeWidth={7} strokeLinejoin="round" />
				<polygon points={octagon(81)} fill="none" stroke={ink} strokeWidth={2.5} strokeLinejoin="round" />
				{arrival(100, 54, 40)}
				<text {...text(Math.min(34, 190 / len))} x={100} y={116}>
					{stamp.city}
				</text>
				<text {...text(14, 700)} letterSpacing={1.5} x={100} y={142}>
					{stamp.when}
				</text>
				<text {...text(13, 700)} letterSpacing={1.5} x={100} y={160}>
					{stamp.nights}
				</text>
			</>
		);
	}
	const random = scatteredRandom(seed * 97 + 13);
	return (
		<svg width={width} height={(width * vbH) / vbW} viewBox={`0 0 ${vbW} ${vbH}`} style={{ display: "block", overflow: "visible" }}>
			<defs>
				<filter id={`${id}-ink`} x="-6%" y="-6%" width="112%" height="112%">
					<feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves={2} seed={seed} result="grain" />
					<feColorMatrix in="grain" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -3.4 2.55" result="holes" />
					<feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves={1} seed={seed + 9} result="patch" />
					<feColorMatrix in="patch" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -2.2 2.0" result="pressure" />
					<feComposite in="holes" in2="pressure" operator="in" result="mask" />
					<feComposite in="SourceGraphic" in2="mask" operator="in" result="worn" />
					<feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves={1} seed={seed + 5} result="warp" />
					<feDisplacementMap in="worn" in2="warp" scale={4} xChannelSelector="R" yChannelSelector="G" />
				</filter>
			</defs>
			<g filter={`url(#${id}-ink)`}>
				{body}
				{/* A few specks of ink thrown off round the rim. */}
				{specks
					? Array.from({ length: 7 }, (_, i) => {
							const a = random() * Math.PI * 2;
							const r = 1.03 + random() * 0.14;
							return <circle key={i} cx={vbW / 2 + (Math.cos(a) * vbW * r) / 2} cy={vbH / 2 + (Math.sin(a) * vbH * r) / 2} r={1 + random() * 2.6} fill={ink} />;
						})
					: null}
			</g>
		</svg>
	);
};

/**
 * A stamp at its slam. The slam scales it by its own width, never by a CSS transform: Chrome lays SVG
 * text out at the scale it first appears at and does not redo it when an HTML ancestor's transform
 * changes, so a stamp that landed from 1.9x kept its text 1.3x too big, pushed off its border and cut
 * by the ink filter's box. A still, mounted fresh, never showed it; only a rendered video did. Keyed
 * per slam step, so each step is laid out anew.
 */
const StampAt = ({
	frame,
	at,
	stamp,
	width,
	id,
	seed,
	rotate,
	ink,
	onPhoto = false,
}: {
	frame: number;
	at: number;
	stamp: PassportStamp;
	width: number;
	id: string;
	seed: number;
	rotate: number;
	ink?: string;
	onPhoto?: boolean;
}) => {
	const slam = slamAt(frame, at);
	if (!slam) return null;
	const [vbW, vbH] = VIEWBOX[stamp.shape];
	const height = (width * vbH) / vbW;
	const w = width * slam.scale;
	return (
		<div
			style={{
				position: "relative",
				width,
				height,
				transform: `rotate(${rotate}deg)`,
				// Ink on paper darkens it; on a photo a white stamp needs a little shadow to stand off it.
				mixBlendMode: onPhoto ? "normal" : "multiply",
				filter: onPhoto ? "drop-shadow(0 0 10px rgba(0,0,0,.45))" : undefined,
			}}
		>
			<div
				key={Math.min(frame - at, 3)}
				style={{ position: "absolute", left: (width - w) / 2, top: (height - (height * w) / width) / 2, opacity: slam.opacity }}
			>
				<Stamp stamp={stamp} width={w} id={id} seed={seed} ink={ink} />
			</div>
		</div>
	);
};

/** Fine wavy security lines: a passport page, not a sheet of paper. */
const SecurityLines = ({ width, height, px, rows }: { width: number; height: number; px: (n: number) => number; rows: number }) => (
	<svg width={width} height={height} style={{ position: "absolute", inset: 0 }}>
		{Array.from({ length: rows }, (_, i) => {
			const y0 = (height * (i + 0.5)) / rows;
			const d = Array.from(
				{ length: 49 },
				(_, s) => `${s ? "L" : "M"}${((width * s) / 48).toFixed(1)} ${(y0 + Math.sin((s / 48) * Math.PI * 4 + i * 0.7) * height * 0.016).toFixed(1)}`
			).join(" ");
			return <path key={i} d={d} fill="none" stroke={i % 3 ? TEAL : "#13C892"} strokeOpacity={0.08} strokeWidth={px(3)} />;
		})}
	</svg>
);

type PassportProps = {
	frame: number;
	width: number;
	height: number;
	px: (n: number) => number;
	layout: IntroLayout;
	reelData: any[];
	currentTrip: any;
	nightsText: string;
	handwriting: string;
	/** The reel's header, which the intro fades itself. */
	words: React.ReactNode;
	/** The header's scrim, for when a photo is under it. */
	scrim: React.ReactNode;
};

/** Where the passport and the stamps may stand: under the header, with room between them. */
const stage = ({ layout, width, height }: PassportProps) => overlayBox(layout, width, height);

// ---- passport-flip ---------------------------------------------------------------------------------

const PassportFlip = (p: PassportProps) => {
	const { frame, px, reelData, currentTrip, nightsText, handwriting, words } = p;
	const all = passportStamps(reelData, currentTrip);
	const stamps = pickEvenly(all, PASSPORT_PAGES);
	const n = stamps.length;
	const hits = stampHits(n, PF_STAMPS, PF_EVERY);
	const room = stage(p);
	const H = Math.min(px(660), room.height * 0.78);
	const W = H * 0.712;
	const cx = room.left + room.width / 2;
	const top = room.top + (room.height - H) / 2 - px(30);

	const drop = 1 - ease(frame, PF_DROP, Easing.out(Easing.back(1.2)));
	const open = ease(frame, PF_OPEN);
	const turned = (j: number) => (j < n - 1 ? ease(frame, [hits[j] + PF_TURN[0], hits[j] + PF_TURN[1]]) : 0);
	const leave = ease(frame, PF_LEAVE, Easing.in(Easing.cubic));
	const [dx, dy] = shakeAt(frame, hits, px(12));
	const stamped = hits.filter((h) => frame >= h).length;
	const r = px(18);

	const face = (right: boolean, extra: React.CSSProperties = {}): React.CSSProperties => ({
		position: "absolute",
		inset: 0,
		overflow: "hidden",
		backfaceVisibility: "hidden",
		WebkitBackfaceVisibility: "hidden",
		borderRadius: right ? `0 ${r}px ${r}px 0` : `${r}px 0 0 ${r}px`,
		...extra,
	});
	const paper = (right: boolean) =>
		face(right, { background: PAPER, boxShadow: `inset ${right ? px(18) : -px(18)}px 0 ${px(24)}px -${px(12)}px rgba(0,0,0,.18)` });
	/** A leaf hinged on the spine; its back is the left-hand page once it has turned. */
	const leaf = (key: string, order: number, turn: number, front: React.ReactNode, frontStyle: React.CSSProperties, back: React.ReactNode) => (
		<div
			key={key}
			style={{
				position: "absolute",
				left: 0,
				top: 0,
				width: W,
				height: H,
				zIndex: leafZ(order, turn),
				transformOrigin: "0 50%",
				transformStyle: "preserve-3d",
				transform: `perspective(${px(2400)}px) rotateY(${-180 * turn}deg)`,
			}}
		>
			<div style={frontStyle}>{front}</div>
			<div style={{ ...paper(false), transform: "rotateY(180deg)" }}>{back}</div>
		</div>
	);

	const countries = (currentTrip?.countries || []).map((c: any) => c?.name).filter(Boolean);
	const coverName = countries.length > 1 ? `${countries.length} COUNTRIES` : String(countries[0] || "").toUpperCase();
	const from = moment.utc(currentTrip?.startDate ?? null);
	const to = moment.utc(currentTrip?.endDate ?? null);
	const dated = from.isValid() && to.isValid() && !currentTrip?.isTemplate;
	const label = { fontFamily: "Poppins", fontWeight: 700, fontSize: px(18), letterSpacing: "0.3em", color: "rgba(15,62,74,.5)" };
	const hand = { fontFamily: handwriting, fontWeight: 700, fontSize: px(58), lineHeight: 1.1, color: TEAL };

	const cover = leaf(
		"cover",
		-1,
		open,
		<>
			<div style={{ fontSize: px(34), letterSpacing: "0.34em" }}>PASSPORT</div>
			<svg width={px(170)} height={px(170)} viewBox="0 0 100 100">
				<g fill="none" stroke={GOLD} strokeWidth={3}>
					<circle cx={50} cy={50} r={44} />
					<ellipse cx={50} cy={50} rx={20} ry={44} />
					<path d="M6 50 H94 M13 28 H87 M13 72 H87" />
				</g>
			</svg>
			<div style={{ fontSize: px(coverName.length > 10 ? 36 : 46), letterSpacing: "0.12em", textAlign: "center" }}>{coverName}</div>
			<div style={{ fontSize: px(22), letterSpacing: "0.3em", opacity: 0.8 }}>STIPPL</div>
		</>,
		face(true, {
			background: `linear-gradient(160deg, #13505e, ${TEAL})`,
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: px(34),
			color: GOLD,
			fontFamily: "Poppins",
			fontWeight: 700,
		}),
		// Inside the cover: the trip's own dates, as the passport's validity.
		<div style={{ position: "absolute", left: px(40), right: px(30), top: H * 0.3, transform: "rotate(-4deg)" }}>
			{dated ? (
				<>
					<div style={label}>FROM</div>
					<div style={hand}>{from.format("D MMM YYYY")}</div>
					<div style={{ ...label, marginTop: px(24) }}>TO</div>
					<div style={hand}>{to.format("D MMM YYYY")}</div>
				</>
			) : (
				<>
					<div style={label}>VALID FOR</div>
					<div style={hand}>{nightsText}</div>
				</>
			)}
		</div>
	);

	const pages = stamps.map((stamp, j) => {
		const rotate = (scatteredRandom(j + 40)() - 0.5) * 22;
		const sw = W * (stamp.shape === "rect" ? 0.86 : 0.74);
		const sh = (sw * VIEWBOX[stamp.shape][1]) / VIEWBOX[stamp.shape][0];
		const at = { position: "absolute" as const, left: (W - sw) / 2, top: (H - sh) / 2 };
		return leaf(
			`page-${j}`,
			j,
			turned(j),
			<>
				<SecurityLines width={W} height={H} px={px} rows={12} />
				<div style={{ ...label, position: "absolute", right: px(26), top: px(22), fontSize: px(20), letterSpacing: "0.2em" }}>
					{String(j + 1).padStart(2, "0")}
				</div>
				<div style={at}>
					<StampAt frame={frame} at={hits[j]} stamp={stamp} width={sw} id={`passport-flip-${j}`} seed={j + 11} rotate={rotate} />
				</div>
			</>,
			paper(true),
			// The ink shows through, faintly, on the back of the page.
			frame >= hits[j] ? (
				<div style={{ ...at, transform: `scaleX(-1) rotate(${rotate}deg)`, opacity: 0.1 }}>
					<Stamp stamp={stamp} width={sw} id={`passport-flip-back-${j}`} seed={j + 11} specks={false} />
				</div>
			) : null
		);
	});

	return (
		<AbsoluteFill>
			<AbsoluteFill style={{ ...blurredMap(px, 0.6), opacity: 1 - ease(frame, PF_LEAVE) }} />
			<div
				style={{
					position: "absolute",
					// Closed, the cover is centred; open, the spine is.
					left: cx - (W / 2) * (1 - open) + dx,
					// It drops in from below and, done, falls out the bottom of the frame, tipping away.
					top: top + dy + drop * p.height * 0.7 + leave * p.height * 0.9,
					width: W,
					height: H,
					transformOrigin: "0 100%",
					transform: `perspective(${px(2600)}px) rotateX(${leave * 35}deg) rotate(${-4 * drop + 8 * leave}deg)`,
				}}
			>
				{/* The back cover under the right-hand pages, and the front cover's edge under the left once open. */}
				<div
					style={{
						position: "absolute",
						left: -px(4),
						top: -px(6),
						width: W + px(10),
						height: H + px(12),
						borderRadius: `0 ${r}px ${r}px 0`,
						background: TEAL,
						boxShadow: `0 ${px(30)}px ${px(70)}px rgba(0,0,0,.5)`,
					}}
				/>
				{open > 0.5 ? (
					<div
						style={{
							position: "absolute",
							left: -W - px(6),
							top: -px(6),
							width: W + px(6),
							height: H + px(12),
							borderRadius: `${r}px 0 0 ${r}px`,
							background: TEAL,
						}}
					/>
				) : null}
				{pages}
				{cover}
			</div>
			<div
				style={{
					position: "absolute",
					left: room.left,
					width: room.width,
					top: top + H + px(50),
					textAlign: "center",
					fontFamily: "Poppins",
					fontWeight: 800,
					fontSize: px(56),
					color: "#fff",
					textShadow: `0 ${px(4)}px ${px(20)}px rgba(0,0,0,.5)`,
					opacity: stamped ? 1 - ease(frame, [PF_LEAVE[0] - 4, PF_LEAVE[0] + 6]) : 0,
				}}
			>
				{/* Counted by stop, so a ten-stop trip with seven pages still ends on 10/10, as its header says. */}
				{stamped ? `${stamps[stamped - 1].stop + 1}/${all.length}` : null}
			</div>
			{words}
		</AbsoluteFill>
	);
};

// ---- passport-photos -------------------------------------------------------------------------------

/** Where the stamps land together, as fractions of the stage: x, y, turn, width, shape. */
const TOGETHER: [number, number, number, number, StampShape][] = [
	[0.04, 0.0, -10, 0.34, "circle"],
	[0.5, 0.03, 7, 0.42, "rect"],
	[0.06, 0.37, 5, 0.42, "rect"],
	[0.6, 0.3, -8, 0.32, "octagon"],
	[0.02, 0.66, -4, 0.32, "octagon"],
	[0.36, 0.6, 9, 0.32, "circle"],
	[0.66, 0.68, -12, 0.3, "rect"],
];

const PassportPhotos = (p: PassportProps) => {
	const { frame, px, reelData, currentTrip, words, scrim } = p;
	const stamps = passportStamps(reelData, currentTrip);
	const shots = montageShots(reelData, PP_SHOTS);
	const shotLength = shots.length ? Math.max(PP_SHOT[0], Math.min(PP_SHOT[1], Math.floor((PP_MONTAGE[1] - PP_MONTAGE[0]) / shots.length))) : 0;
	const montageEnd = PP_MONTAGE[0] + shots.length * shotLength;
	const room = stage(p);

	if (frame < montageEnd) {
		const i = Math.floor((frame - PP_MONTAGE[0]) / shotLength);
		const shot = shots[i];
		const start = PP_MONTAGE[0] + i * shotLength;
		const stamp = stamps[shot.stop];
		const sw = room.width * (stamp.shape === "rect" ? 0.62 : 0.52);
		const [dx, dy] = shakeAt(frame, [start + 2], px(18));
		return (
			<AbsoluteFill style={{ background: "#000" }}>
				<AbsoluteFill style={{ transform: `translate(${dx}px, ${dy}px) scale(${1.12 - ((frame - start) / shotLength) * 0.1})` }}>
					<Img src={mediaUrl(shot.photo)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				</AbsoluteFill>
				{scrim}
				<div style={{ position: "absolute", left: room.left + room.width * (i % 2 ? 0.36 : 0.08), top: room.top + room.height * (i % 2 ? 0.42 : 0.18) }}>
					<StampAt frame={frame} at={start + 2} stamp={stamp} width={sw} id={`passport-photo-${i}`} seed={i + 21} rotate={i % 2 ? 9 : -8} ink="#fff" onPhoto />
				</div>
				{words}
			</AbsoluteFill>
		);
	}

	const together = pickEvenly(stamps, TOGETHER.length);
	const hits = stampHits(together.length, [montageEnd + 2, PP_LEAVE[0] - 10], PP_EVERY);
	const [dx, dy] = shakeAt(frame, hits, px(18));
	const out = ease(frame, PP_LEAVE);
	return (
		<AbsoluteFill>
			<AbsoluteFill style={{ ...blurredMap(px, 0.55), opacity: 1 - out }} />
			<AbsoluteFill style={{ transform: `translate(${dx}px, ${dy}px)`, opacity: 1 - out }}>
				{together.map((stamp, j) => {
					const [x, y, rotate, size, shape] = TOGETHER[j];
					return (
						<div key={j} style={{ position: "absolute", left: room.left + x * room.width, top: room.top + y * room.height }}>
							<StampAt
								frame={frame}
								at={hits[j]}
								stamp={{ ...stamp, shape }}
								width={room.width * size}
								id={`passport-together-${j}`}
								seed={j + 31}
								rotate={rotate}
								ink="#fff"
								onPhoto
							/>
						</div>
					);
				})}
			</AbsoluteFill>
			{words}
		</AbsoluteFill>
	);
};

export const PassportIntro = ({ style, ...p }: PassportProps & { style: "passport-flip" | "passport-photos" }) =>
	style === "passport-flip" ? <PassportFlip {...p} /> : <PassportPhotos {...p} />;
